import { useOutletContext } from "react-router-dom";

type Ctx = { openModal: () => void };

export function PricingPage() {
  const { openModal } = useOutletContext<Ctx>();
  return (
    <>
      <section className="pricing-hero">
        <div className="container">
          <span className="eyebrow">Pricing</span>
          <h1>Honest pricing,<br /><em>defined before work begins.</em></h1>
          <p className="pricing-lead">
            Every engagement starts with discovery and ends with a written scope and quote.
            The numbers below are starting points — your actual quote depends on the work.
          </p>
          <button className="btn btn-primary" onClick={openModal}>
            Book a free intro
          </button>
        </div>
      </section>

      <section className="section pricing-section" id="how-we-engage">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">How we engage</span>
            <h2>Discovery first.<br /><em>Scope second.</em> Code third.</h2>
            <p>The same pattern on every project. No surprise invoices, no creep.</p>
          </div>
          <div className="engagement-grid">
            <div className="engagement-card">
              <h3>Discovery first</h3>
              <p>
                Before any code is written, we sit down and define the problem,
                the scope, and the deliverables in plain English. You'll see
                exactly what you're buying.
              </p>
            </div>
            <div className="engagement-card">
              <h3>Deposit at signing</h3>
              <p>
                A deposit is required to start work. The amount depends on the
                project size and is set during scoping.
              </p>
            </div>
            <div className="engagement-card">
              <h3>Certified check or wire only</h3>
              <p>
                We don't take credit cards or online payment processors. Keeps
                processing fees out of your invoice and the paper trail clean.
              </p>
            </div>
            <div className="engagement-card">
              <h3>Scope is scope</h3>
              <p>
                If new work comes up mid-project, that's a new scoping
                conversation — new discovery, new estimate. No surprise creep
                on the original quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section pricing-section pricing-summary" id="summary">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">At a glance</span>
            <h2>What things <em>start at.</em></h2>
            <p>
              Starting prices for each service category. Final quotes are
              defined during discovery — see "How we engage" above.
            </p>
          </div>

          <div className="price-table-wrap">
            <table className="price-table">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col" className="num">Starting at</th>
                  <th scope="col">What it covers</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Quick-Win Fixes <span className="th-note">(1-hr min)</span></th>
                  <td className="num">$350/hr</td>
                  <td>Single defined items — AI review hour, broken script diagnostic, configuration tweak, single-question consult.</td>
                </tr>
                <tr>
                  <th scope="row">Technical Reviews</th>
                  <td className="num">$950</td>
                  <td>Half-day software-stack or workflow review with a plain-English write-up.</td>
                </tr>
                <tr id="ai-build-review">
                  <th scope="row">AI Build Review</th>
                  <td className="num">$1,500</td>
                  <td>Half-day to full-day audit of an AI-generated app for production safety. See detailed tiers below.</td>
                </tr>
                <tr>
                  <th scope="row">Automation Projects</th>
                  <td className="num">$2,500–$5,000</td>
                  <td>Scripts, reconciliations, transforms. Upper end is a cloud-deployed automation (Azure Function or Container App Job) with a defined trigger.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Environment Setup</th>
                  <td className="num">$2,500</td>
                  <td>Basic cloud environment setup. See cloud detail below.</td>
                </tr>
                <tr>
                  <th scope="row">Integrations</th>
                  <td className="num">$3,500</td>
                  <td>API, SaaS, system-to-system glue.</td>
                </tr>
                <tr>
                  <th scope="row">Legacy Modernization</th>
                  <td className="num">$3,500</td>
                  <td>Stabilize, replace, or modernize aging systems (VB6, Access, classic ASP, ColdFusion, old .NET).</td>
                </tr>
                <tr>
                  <th scope="row">Print &amp; Mail Workflow Consulting</th>
                  <td className="num">$3,500</td>
                  <td>Production mail and document workflow analysis, presort review, vendor coordination.</td>
                </tr>
                <tr>
                  <th scope="row">Custom Software</th>
                  <td className="num">$10,000</td>
                  <td>Production apps — multiple components, persistence, UI, advanced project structure.</td>
                </tr>
                <tr>
                  <th scope="row">Ongoing Support Plans</th>
                  <td className="num">$500/mo</td>
                  <td>Monitoring, updates, troubleshooting, cost review, support hours. Includes Azure operations support.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section pricing-section" id="cloud-environment">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Cloud environment setup &amp; hosting enablement</span>
            <h2>Buying a working environment,<br /><em>not a pile of resources.</em></h2>
            <p>
              When you engage us for Azure work, you're buying a configured,
              deployable environment — not a list of services we provisioned
              and walked away from. The tiers below break out by scope.
            </p>
          </div>

          <div className="price-table-wrap">
            <table className="price-table">
              <thead>
                <tr>
                  <th scope="col">Offering</th>
                  <th scope="col" className="num">Starting at</th>
                  <th scope="col">Best for</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Azure Consultation</th>
                  <td className="num">$750</td>
                  <td>Reviewing whether Azure is the right fit and identifying likely services.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Cost Estimate &amp; Architecture Plan</th>
                  <td className="num">$1,500</td>
                  <td>Designing the environment and estimating monthly Azure costs.</td>
                </tr>
                <tr>
                  <th scope="row">Basic Azure Environment Setup</th>
                  <td className="num">$2,500</td>
                  <td>Resource group, SWA, DNS guidance, basic security, simple deployment path.</td>
                </tr>
                <tr>
                  <th scope="row">Small Business Azure Launch</th>
                  <td className="num">$5,000–$8,500</td>
                  <td>SWA, database, storage, monitoring, backup basics, deployment workflow.</td>
                </tr>
                <tr>
                  <th scope="row">Production Azure App Environment</th>
                  <td className="num">$10,000–$25,000</td>
                  <td>SWA/App Service, database, private networking, CI/CD, monitoring, security hardening.</td>
                </tr>
                <tr>
                  <th scope="row">Azure VM Environment Setup</th>
                  <td className="num">$3,500–$12,000</td>
                  <td>Windows/Linux VMs, networking, disks, backups, access controls, basic hardening.</td>
                </tr>
                <tr>
                  <th scope="row">Hybrid / Private Azure Environment</th>
                  <td className="num">$15,000–$50,000+</td>
                  <td>Private networking, VPN, internal apps, private DNS, VM/app/database connectivity.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Migration Project</th>
                  <td className="num">$10,000–$75,000+</td>
                  <td>Moving existing apps, databases, file workflows, or services into Azure.</td>
                </tr>
                <tr>
                  <th scope="row">Ongoing Azure Operations Support</th>
                  <td className="num">$500–$3,500/mo</td>
                  <td>Monitoring review, updates, troubleshooting, cost review, support hours.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="pricing-subhead" id="azure-swa">Azure Static Web App setup</h3>
          <div className="price-table-wrap">
            <table className="price-table compact">
              <tbody>
                <tr>
                  <th scope="row">Basic SWA deployment setup</th>
                  <td className="num">$1,500–$3,500</td>
                </tr>
                <tr>
                  <th scope="row">SWA with custom domain and DNS guidance</th>
                  <td className="num">$2,500–$4,500</td>
                </tr>
                <tr>
                  <th scope="row">SWA with API / backend integration</th>
                  <td className="num">$4,500–$12,000</td>
                </tr>
                <tr>
                  <th scope="row">SWA with auth, environment separation, CI/CD</th>
                  <td className="num">$7,500–$18,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="pricing-subhead" id="azure-database">Azure database setup</h3>
          <div className="price-table-wrap">
            <table className="price-table compact">
              <tbody>
                <tr>
                  <th scope="row">Basic Azure SQL setup</th>
                  <td className="num">$1,500–$3,500</td>
                </tr>
                <tr>
                  <th scope="row">Database migration planning</th>
                  <td className="num">$2,500–$7,500</td>
                </tr>
                <tr>
                  <th scope="row">Small database migration</th>
                  <td className="num">$4,500–$12,000</td>
                </tr>
                <tr>
                  <th scope="row">Production database setup with backups / security</th>
                  <td className="num">$5,000–$15,000</td>
                </tr>
                <tr>
                  <th scope="row">SQL performance / cost review</th>
                  <td className="num">$950–$3,500</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="pricing-subhead" id="azure-vm">Azure VM setup</h3>
          <div className="price-table-wrap">
            <table className="price-table compact">
              <tbody>
                <tr>
                  <th scope="row">Single VM setup</th>
                  <td className="num">$2,500–$5,000</td>
                </tr>
                <tr>
                  <th scope="row">Hardened VM with backups and monitoring</th>
                  <td className="num">$4,500–$8,500</td>
                </tr>
                <tr>
                  <th scope="row">Multi-VM environment</th>
                  <td className="num">$8,500–$20,000</td>
                </tr>
                <tr>
                  <th scope="row">Windows Server app hosting environment</th>
                  <td className="num">$5,000–$15,000</td>
                </tr>
                <tr>
                  <th scope="row">Legacy application VM migration</th>
                  <td className="num">$7,500–$25,000+</td>
                </tr>
              </tbody>
            </table>
          </div>

          <aside className="pricing-callout" role="note">
            <strong>Azure usage is billed by Microsoft, not by us.</strong>{" "}
            Azure hosting, database, storage, bandwidth, backup, and compute
            charges are billed separately by Microsoft Azure. We can estimate
            those costs during planning, but actual monthly usage depends on
            the selected services, region, traffic, storage, uptime, and
            customer-specific usage patterns. The official Azure Pricing
            Calculator is the right place to size real consumption for each
            client.
          </aside>
        </div>
      </section>

      <section className="section pricing-section" id="ai-build-review-detail">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">AI build review tiers</span>
            <h2>Audit AI-generated code<br /><em>before it ships.</em></h2>
            <p>
              Same starting offering as the row above, broken down by intensity.
              Every tier ends with a plain-English verdict and a written list
              of findings.
            </p>
          </div>
          <div className="price-table-wrap">
            <table className="price-table">
              <thead>
                <tr>
                  <th scope="col">Tier</th>
                  <th scope="col" className="num">Price</th>
                  <th scope="col">What you get</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">AI Review Spot Check</th>
                  <td className="num">$1,500</td>
                  <td>Half-day review of a small AI-generated app or feature. Plain-English verdict + critical findings. Go / no-go for production.</td>
                </tr>
                <tr>
                  <th scope="row">Production Readiness Audit</th>
                  <td className="num">$3,500–$7,500</td>
                  <td>Full audit — dependencies, security holes, error paths, data handling, deployment story, observability. Written report + walkthrough call.</td>
                </tr>
                <tr>
                  <th scope="row">Audit + Remediation</th>
                  <td className="num">$7,500–$25,000</td>
                  <td>The audit, plus implementing the critical fixes. Scoped after the audit.</td>
                </tr>
                <tr>
                  <th scope="row">Audit → Rebuild or Re-host</th>
                  <td className="num">Quoted</td>
                  <td>If the app shouldn't be production at all, we estimate the rebuild or re-host as a follow-up under Custom Software or Azure pricing.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section pricing-section" id="packages">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Suggested packages</span>
            <h2>Common shapes,<br /><em>defined up front.</em></h2>
            <p>
              Bundles we've seen requested often. Each is a starting point —
              the final scope and price are set during discovery.
            </p>
          </div>
          <div className="price-table-wrap">
            <table className="price-table">
              <thead>
                <tr>
                  <th scope="col">Package</th>
                  <th scope="col" className="num">Starting at</th>
                  <th scope="col">Includes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Azure Starter Setup</th>
                  <td className="num">$2,500+</td>
                  <td>Resource group, basic SWA / app hosting setup, DNS guidance, deployment notes.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Business Foundation</th>
                  <td className="num">$5,000+</td>
                  <td>App hosting, database / storage setup, environment organization, basic monitoring.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Production Ready</th>
                  <td className="num">$10,000+</td>
                  <td>CI/CD, backups, security hardening, monitoring, access controls, documentation.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Private Business Environment</th>
                  <td className="num">$15,000+</td>
                  <td>Internal / private hosting, VM / database connectivity, private DNS / networking guidance.</td>
                </tr>
                <tr>
                  <th scope="row">Azure Migration Roadmap</th>
                  <td className="num">$3,500+</td>
                  <td>Review current systems, recommend Azure services, estimate cost, plan migration phases.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="contact-cta">
        <div className="container">
          <div className="contact-band">
            <div>
              <h2>None of these quite fit?</h2>
              <p>
                Bring the question, project, or app that doesn't fit a neat
                row. Discovery is always the first step — and the first call
                is free.
              </p>
            </div>
            <div className="right">
              <button className="btn btn-petal" onClick={openModal}>
                Book a free intro
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
